import {
	clearSessionData,
	getAuth,
	getBusinessData,
	saveAuth,
	saveBusinessData,
} from '@workinance/shared/database';
import { type IAuthenticatedUser } from '@workinance/shared/interfaces/auth.interface';
import { type IBusiness } from '@workinance/shared/interfaces/business.interface';

export interface IAuthData {
	token: string;
	/** Renews `token` when it expires. Saved with it, in the same record. */
	refreshToken?: string;
	user: IAuthenticatedUser;
	/** The business the session works in; every request is scoped to it. */
	business?: IBusiness | null;
	/** Every business an owner has, for switching between them. A team member only has their own. */
	businesses?: IBusiness[];
}

/** Who signed in last on this device. The business code, business name and username are not secrets. */
export interface ILastSignIn {
	businessCode: string;
	/** Missing when only a shared link told us the business. */
	businessName?: string;
	username: string;
}

/** Checks what came out of storage really is a remembered sign-in, since stored data can be stale or edited. */
export function toLastSignIn(stored: unknown): ILastSignIn | null {
	const candidate = stored as Partial<ILastSignIn> | null;
	if (
		typeof candidate?.businessCode !== 'string' ||
		typeof candidate?.username !== 'string'
	) {
		return null;
	}
	return {
		businessCode: candidate.businessCode,
		username: candidate.username,
		businessName:
			typeof candidate.businessName === 'string'
				? candidate.businessName
				: undefined,
	};
}

type IAuthRecord = Pick<IAuthData, 'token' | 'refreshToken' | 'user'>;
type IBusinessRecord = Omit<IAuthData, 'token' | 'refreshToken' | 'user'>;

/** Saves the session as two records: who is signed in, and which business it works with. */
export async function saveAuthData<TAuthData extends IAuthData = IAuthData>(
	authData: TAuthData,
): Promise<void> {
	const { token, refreshToken, user, ...businessData } = authData;
	// Most saves are the same session with something else changed and do not carry the refresh token,
	// so the one already stored is kept rather than overwritten.
	const keptRefreshToken =
		refreshToken ?? (await getAuth<IAuthRecord>())?.refreshToken;
	await Promise.all([
		saveAuth<IAuthRecord>({ token, refreshToken: keptRefreshToken, user }),
		saveBusinessData<IBusinessRecord>(businessData),
	]);
}

/** Reads the two records back as one session. Null when nobody is signed in. */
export async function getAuthData<
	TAuthData extends IAuthData = IAuthData,
>(): Promise<TAuthData | null> {
	const [auth, businessData] = await Promise.all([
		getAuth<IAuthRecord>(),
		getBusinessData<IBusinessRecord>(),
	]);
	return auth ? ({ ...businessData, ...auth } as TAuthData) : null;
}

/** Clears the session (auth and business). What the device remembers about the last sign in stays. */
export async function clearAuthData(): Promise<void> {
	await clearSessionData();
}
